import { useState } from "react";
import AnimatedSection from "@/components/base/AnimatedSection";
import { caseGroups } from "@/mocks/home";

const categoryMeta: Record<string, { icon: string; iconBg: string; check: string }> = {
  마약사건: {
    icon: "ri-capsule-line",
    iconBg: "bg-primary-100 text-primary-600",
    check: "bg-primary-100 text-primary-700",
  },
  교통사고: {
    icon: "ri-steering-2-line",
    iconBg: "bg-secondary-100 text-secondary-700",
    check: "bg-secondary-100 text-secondary-800",
  },
  성범죄: {
    icon: "ri-shield-star-line",
    iconBg: "bg-accent-100 text-accent-700",
    check: "bg-accent-100 text-accent-800",
  },
  소년범죄: {
    icon: "ri-user-heart-line",
    iconBg: "bg-background-200 text-foreground-600",
    check: "bg-background-200 text-foreground-700",
  },
  부동산: {
    icon: "ri-building-2-line",
    iconBg: "bg-primary-100/80 text-primary-600",
    check: "bg-primary-100/80 text-primary-700",
  },
  경제범죄: {
    icon: "ri-bank-line",
    iconBg: "bg-accent-100 text-accent-700",
    check: "bg-accent-100 text-accent-800",
  },
};

const fallbackMeta = {
  icon: "ri-folder-line",
  iconBg: "bg-background-200 text-foreground-600",
  check: "bg-background-200 text-foreground-700",
};

export default function CasesSection() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const toggleCategory = (category: string) => {
    setSelectedCategory((prev) => (prev === category ? null : category));
  };

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-accent-500 text-sm font-medium tracking-widest mb-2">CASES</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
            <span className="text-primary-500">수행사건</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            부동산·형사전문 이승환 변호사가 수행한 주요 성공 사례입니다
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-3">
          {caseGroups.map((group, idx) => {
            const meta = categoryMeta[group.category] || fallbackMeta;
            const isOpen = selectedCategory === group.category;
            return (
              <AnimatedSection key={group.category} delay={idx * 60}>
                <div
                  className={`bg-background-50 rounded-xl border overflow-hidden transition-colors duration-300 ${
                    isOpen ? "border-primary-300/70" : "border-background-200/60"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleCategory(group.category)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center gap-3 p-5 md:p-6 text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  >
                    <span className={`w-11 h-11 flex items-center justify-center rounded-lg shrink-0 ${meta.iconBg}`}>
                      <i className={`${meta.icon} text-xl`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg md:text-xl font-bold text-foreground-950">
                        {group.category}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 mt-0.5">{group.description}</p>
                    </div>
                    <span
                      className={`w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 transition-transform duration-300 shrink-0 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-lg" />
                    </span>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 md:px-6 pb-6 md:pb-7">
                        <div className="border-t border-background-200/60 pt-5 space-y-3">
                          {group.items.map((item) => (
                            <div key={item} className="flex items-start gap-2.5">
                              <span className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${meta.check}`}>
                                <i className="ri-check-line text-xs" />
                              </span>
                              <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}